import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { TOKEN_EXPIRED } from '@speeki/dictionary';
import {
    Button,
    notify,
    PinField,
    StepControl,
} from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { Card } from '@components/molecules/Card/Card';
import { ColumnsLayout } from '@components/templates/ColumnsLayout/ColumnsLayout';

import { MfaResetRequestDocument } from '@graphql/generated/graphql';

import { getFingerprint } from '@utils/fingerprint';

import { AuthService } from '@services/AuthService/AuthService';
import { useLoginMfa } from '@services/AuthService/useLoginMfa';
import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { StepControlWrapper } from '@domains/auth/ions/cards';
import { LoginBanner } from '@domains/auth/molecules/LoginBanner/LoginBanner';
import { yupResolver } from '@hookform/resolvers/yup';

import {
    CardWrapper,
    EnterCodeCheckbox,
    PinWrapper,
    ResetButtonWrapper,
} from './EnterCodePage.styled';

export const EnterCodePage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        clearErrors,
        control,
        formState: { errors, isSubmitted, isSubmitting },
        handleSubmit,
        register,
        setError,
    } = useForm<MfaLoginFormValues>({
        reValidateMode: 'onSubmit',
        resolver: yupResolver(MfaLoginFormSchema),
    });

    const { handleLogin, loginError } = useLoginMfa();
    const [resetRequest, { loading: requestLoading }] = useMutation(
        MfaResetRequestDocument,
        {
            context: {
                notifyOnError: {
                    exceptions: [TOKEN_EXPIRED],
                },
            },
            onCompleted: () => {
                void AuthService.logout(false);
                navigate(getPath({ path: '/auth/request-reset-mfa' }));
                notify(t('MFA_RESET_MESSAGE')).INFO();
            },
            onError: () => {},
        },
    );

    const {
        field: { onChange, ...field },
    } = useController({ control, name: 'code' });

    const onSubmit = handleSubmit(async (data) => {
        const fingerprint = await getFingerprint();

        await handleLogin({
            input: {
                code: data.code,
                fingerprint: data.rememberMe ? fingerprint : null,
            },
        });
    });

    useEffect(() => {
        loginError?.graphQLErrors[0]?.extensions?.code === 'UNAUTHENTICATED' &&
            setError('code', {
                message: 'INVALID_CODE',
                type: 'invalid-code',
            });
    }, [loginError]);

    return (
        <ColumnsLayout>
            <CardWrapper testId="ayjpQ1A">
                <form onSubmit={onSubmit}>
                    <Card.Title>{t('ENTER_VERIFICATION_CODE')}</Card.Title>
                    <Card.Description>
                        {t('ENTER_6_DIGIT_CODE')}
                    </Card.Description>
                    <PinWrapper>
                        {/*@ts-expect-error TODO: something wrong with the types*/}
                        <PinField
                            className="htmlToImageIgnore"
                            error={!!errors.code}
                            message={t(errors.code?.message || '')}
                            name={field.name}
                            onChange={(value) => {
                                clearErrors('code');
                                onChange(value);
                            }}
                            ref={field.ref}
                            spacing={8}
                            type="number"
                            xsSpacing={6}
                        />
                    </PinWrapper>
                    <EnterCodeCheckbox
                        {...register('rememberMe')}
                        testId="YStO9Wq"
                    >
                        {t('REMEMBER_BROWSER')}
                    </EnterCodeCheckbox>
                    <Button
                        disabled={isSubmitting || (errors.code && isSubmitted)}
                        fullWidth
                        isLoading={isSubmitting}
                        testId="button_log-in-securely"
                        variant="primary"
                    >
                        {t('LOG_IN_SECURELY')}
                    </Button>
                    <StepControlWrapper>
                        <StepControl
                            activeStep={2}
                            length={2}
                            testId="hJi7VCw"
                            variant="dark"
                        />
                    </StepControlWrapper>
                </form>
            </CardWrapper>
            <LoginBanner>
                <IconManager icon={'SvgInfo'} size={16} />
                <LoginBanner.Description>
                    {t('MFA_TROUBLES')}
                </LoginBanner.Description>
                <ResetButtonWrapper>
                    <Button
                        disabled={requestLoading}
                        isLoading={requestLoading}
                        onClick={() => resetRequest()}
                        size="small"
                        testId="button_send-request"
                        variant="secondary"
                    >
                        {t('SEND_REQUEST')}
                    </Button>
                </ResetButtonWrapper>
            </LoginBanner>
        </ColumnsLayout>
    );
};

const MfaLoginFormSchema = yup.object({
    code: yup.string().required('TOO_SHORT_CODE').min(6, 'TOO_SHORT_CODE'),
    rememberMe: yup.boolean(),
});

type MfaLoginFormValues = yup.InferType<typeof MfaLoginFormSchema>;
